import React, { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../../context/LanguageContext';

const API = '/api/v1/fsm';

const ERPImportModal = ({ onClose, onImported }) => {
  const { lang, t } = useLanguage();
  const f = t('fsm');
  const isRTL = lang === 'ar';
  const token = localStorage.getItem('token');
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  const [tab, setTab] = useState('search'); // 'search' | 'status'
  const [search, setSearch] = useState('');
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, total_pages: 0 });
  const [importing, setImporting] = useState({}); // { accountId: 'loading'|'done'|'error' }
  const [syncStatus, setSyncStatus] = useState(null);
  const [bulkSyncing, setBulkSyncing] = useState(false);
  const [bulkProgress, setBulkProgress] = useState(null);

  // Styles matching FSMClients
  const modalOverlay = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 };
  const modalBox = { background: '#1e293b', borderRadius: '16px', padding: '24px', width: '850px', maxHeight: '90vh', overflow: 'auto', border: '1px solid rgba(255,255,255,0.1)', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' };
  const inputStyle = { width: '100%', padding: '10px 12px', borderRadius: '8px', border: '2px solid rgba(255,255,255,0.2)', backgroundColor: '#1a2038', color: '#f9fafb', fontSize: '13px', outline: 'none', boxSizing: 'border-box' };

  // Fetch sync status
  const fetchStatus = useCallback(async () => {
    try {
      const res = await fetch(`${API}/erp-sync/status`, { headers });
      const data = await res.json();
      setSyncStatus(data);
    } catch (err) { console.error('Status fetch error:', err); }
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  // Search ERP clients
  const searchClients = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 20 });
      if (search) params.append('search', search);
      const res = await fetch(`${API}/erp-sync/clients?${params}`, { headers });
      const data = await res.json();
      setClients(data.clients || []);
      setPagination(data.pagination || { page: 1, limit: 20, total: 0, total_pages: 0 });
    } catch (err) { console.error('Search error:', err); }
    setLoading(false);
  }, [search]);

  useEffect(() => {
    if (tab === 'search') {
      const timer = setTimeout(() => searchClients(1), 300);
      return () => clearTimeout(timer);
    }
  }, [search, tab]);

  // Import single client
  const importClient = async (accountId) => {
    setImporting(prev => ({ ...prev, [accountId]: 'loading' }));
    try {
      const res = await fetch(`${API}/erp-sync/import`, {
        method: 'POST', headers,
        body: JSON.stringify({ erp_account_id: accountId }),
      });
      const data = await res.json();
      if (data.success) {
        setImporting(prev => ({ ...prev, [accountId]: 'done' }));
        // Mark as imported in the list
        setClients(prev => prev.map(c => c.id === accountId ? { ...c, is_imported: true } : c));
        fetchStatus();
        if (onImported) onImported();
      } else {
        setImporting(prev => ({ ...prev, [accountId]: 'error' }));
      }
    } catch (err) {
      setImporting(prev => ({ ...prev, [accountId]: 'error' }));
    }
  };

  // Full sync
  const runFullSync = async () => {
    setBulkSyncing(true);
    setBulkProgress({ created: 0, updated: 0, page: 0, total_pages: 0 });
    try {
      // Paginated sync to avoid timeout
      let page = 1;
      const limit = 200;
      let hasMore = true;
      let totals = { created: 0, updated: 0, errors: [] };

      while (hasMore) {
        const res = await fetch(`${API}/erp-sync/import-bulk`, {
          method: 'POST', headers,
          body: JSON.stringify({ page, limit }),
        });
        const data = await res.json();
        totals.created += data.created || 0;
        totals.updated += data.updated || 0;
        if (data.errors) totals.errors.push(...data.errors);
        hasMore = data.has_more;
        setBulkProgress({
          created: totals.created,
          updated: totals.updated,
          errors: totals.errors.length,
          page,
          total_pages: data.pagination?.total_pages || 0,
        });
        page++;
      }

      setBulkProgress(prev => ({ ...prev, done: true }));
      fetchStatus();
      if (onImported) onImported();
    } catch (err) {
      console.error('Full sync error:', err);
      setBulkProgress(prev => ({ ...prev, error: err.message }));
    }
    setBulkSyncing(false);
  };

  const tabStyle = (active) => ({
    padding: '8px 20px', borderRadius: '8px', border: 'none', fontSize: '13px', fontWeight: '600', cursor: 'pointer',
    background: active ? 'rgba(99,102,241,0.2)' : 'transparent',
    color: active ? '#818cf8' : '#94a3b8',
  });

  const importBtnStyle = (state) => {
    if (state === 'done') return { background: 'rgba(34,197,94,0.15)', color: '#22c55e', border: 'none', padding: '4px 12px', borderRadius: '6px', fontSize: '11px', fontWeight: '600', cursor: 'default' };
    if (state === 'loading') return { background: 'rgba(234,179,8,0.15)', color: '#eab308', border: 'none', padding: '4px 12px', borderRadius: '6px', fontSize: '11px', fontWeight: '600', cursor: 'wait' };
    if (state === 'error') return { background: 'rgba(239,68,68,0.15)', color: '#f87171', border: 'none', padding: '4px 12px', borderRadius: '6px', fontSize: '11px', fontWeight: '600', cursor: 'pointer' };
    return { background: 'rgba(99,102,241,0.15)', color: '#818cf8', border: 'none', padding: '4px 12px', borderRadius: '6px', fontSize: '11px', fontWeight: '600', cursor: 'pointer' };
  };

  return (
    <div style={modalOverlay} onClick={onClose}>
      <div style={modalBox} onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h2 style={{ fontSize: '18px', fontWeight: '700', color: '#f1f5f9', margin: 0 }}>
            🔄 {f?.erp_import || 'Import from ERP'}
          </h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#94a3b8', fontSize: '20px', cursor: 'pointer', padding: '4px 8px' }}>✕</button>
        </div>

        {/* Status bar */}
        {syncStatus && (
          <div style={{ display: 'flex', gap: '16px', marginBottom: '16px', padding: '12px 16px', background: 'rgba(15,23,42,0.4)', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.06)', flexWrap: 'wrap' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '20px', fontWeight: '700', color: '#818cf8' }}>{syncStatus.erp_total_clients}</div>
              <div style={{ fontSize: '11px', color: '#94a3b8' }}>{f?.erp_clients || 'ERP Clients'}</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '20px', fontWeight: '700', color: '#22c55e' }}>{syncStatus.fsm_clients?.erp_linked || 0}</div>
              <div style={{ fontSize: '11px', color: '#94a3b8' }}>{f?.imported || 'Imported'}</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '20px', fontWeight: '700', color: '#f59e0b' }}>{syncStatus.fsm_clients?.standalone || 0}</div>
              <div style={{ fontSize: '11px', color: '#94a3b8' }}>{f?.standalone || 'Standalone'}</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '20px', fontWeight: '700', color: '#f1f5f9' }}>{syncStatus.fsm_clients?.total_fsm_clients || 0}</div>
              <div style={{ fontSize: '11px', color: '#94a3b8' }}>{f?.total || 'Total FSM'}</div>
            </div>
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
              <button
                onClick={runFullSync}
                disabled={bulkSyncing}
                style={{
                  background: bulkSyncing ? 'rgba(234,179,8,0.15)' : 'linear-gradient(135deg,#059669,#10b981)',
                  color: bulkSyncing ? '#eab308' : '#fff',
                  border: 'none', padding: '8px 16px', borderRadius: '8px', fontWeight: '600', fontSize: '12px', cursor: bulkSyncing ? 'wait' : 'pointer',
                }}
              >
                {bulkSyncing ? `⏳ ${f?.syncing || 'Syncing'}...` : `🔄 ${f?.full_sync || 'Full Sync'}`}
              </button>
            </div>
          </div>
        )}

        {/* Bulk progress */}
        {bulkProgress && (
          <div style={{ marginBottom: '12px', padding: '10px 14px', background: bulkProgress.done ? 'rgba(34,197,94,0.1)' : 'rgba(234,179,8,0.1)', borderRadius: '8px', border: `1px solid ${bulkProgress.done ? 'rgba(34,197,94,0.2)' : 'rgba(234,179,8,0.2)'}`, fontSize: '12px' }}>
            {bulkProgress.done ? (
              <span style={{ color: '#22c55e' }}>✅ {f?.sync_complete || 'Sync complete'}: {bulkProgress.created} {f?.created || 'created'}, {bulkProgress.updated} {f?.updated || 'updated'}{bulkProgress.errors > 0 ? `, ${bulkProgress.errors} ${f?.errors || 'errors'}` : ''}</span>
            ) : (
              <span style={{ color: '#eab308' }}>⏳ {f?.syncing_page || 'Page'} {bulkProgress.page}/{bulkProgress.total_pages} — {bulkProgress.created} {f?.created || 'created'}, {bulkProgress.updated} {f?.updated || 'updated'}</span>
            )}
          </div>
        )}

        {/* Tabs */}
        <div style={{ display: 'flex', gap: '4px', marginBottom: '16px', background: 'rgba(15,23,42,0.3)', padding: '4px', borderRadius: '10px' }}>
          <button style={tabStyle(tab === 'search')} onClick={() => setTab('search')}>🔍 {f?.search_erp || 'Search ERP'}</button>
        </div>

        {/* Search tab */}
        {tab === 'search' && (
          <>
            <input
              type="text"
              placeholder={f?.search_erp_placeholder || 'Search by name, code, phone, email...'}
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ ...inputStyle, marginBottom: '12px' }}
              autoFocus
            />

            {/* Results table */}
            <div style={{ background: 'rgba(30,41,59,0.5)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '12px', overflow: 'hidden' }}>
              {loading ? (
                <div style={{ padding: '30px', textAlign: 'center', color: '#94a3b8' }}>{f?.loading || 'Loading...'}</div>
              ) : clients.length === 0 ? (
                <div style={{ padding: '30px', textAlign: 'center', color: '#94a3b8' }}>
                  <div style={{ fontSize: '36px', marginBottom: '8px' }}>🔍</div>
                  {search ? (f?.no_erp_results || 'No ERP clients found') : (f?.type_to_search || 'Type to search ERP clients')}
                </div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                      {[f?.code || 'Code', f?.company || 'Company', f?.phone || 'Phone', f?.email || 'Email', f?.balance || 'Balance', f?.subsidiary || 'Subsidiary', ''].map(h => (
                        <th key={h} style={{ padding: '10px 12px', textAlign: isRTL ? 'right' : 'left', fontSize: '11px', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {clients.map(c => {
                      const state = c.is_imported ? 'done' : (importing[c.id] || null);
                      return (
                        <tr key={c.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}
                          onMouseOver={e => e.currentTarget.style.background = 'rgba(99,102,241,0.06)'}
                          onMouseOut={e => e.currentTarget.style.background = 'transparent'}>
                          <td style={{ padding: '8px 12px', fontSize: '12px', color: '#818cf8', fontFamily: 'monospace' }}>{c.client_code}</td>
                          <td style={{ padding: '8px 12px' }}>
                            <div style={{ fontWeight: '600', color: '#f1f5f9', fontSize: '13px' }}>{isRTL && c.name_ar ? c.name_ar : c.name}</div>
                            {c.name_ar && !isRTL && <div style={{ fontSize: '11px', color: '#94a3b8' }}>{c.name_ar}</div>}
                            {c.name && isRTL && <div style={{ fontSize: '11px', color: '#94a3b8' }}>{c.name}</div>}
                          </td>
                          <td style={{ padding: '8px 12px', fontSize: '12px', color: '#cbd5e1' }}>{c.phone || '-'}</td>
                          <td style={{ padding: '8px 12px', fontSize: '12px', color: '#94a3b8' }}>{c.email || '-'}</td>
                          <td style={{ padding: '8px 12px', fontSize: '12px', color: c.account_balance > 0 ? '#22c55e' : '#94a3b8' }}>{c.account_balance != null ? Number(c.account_balance).toLocaleString() : '-'}</td>
                          <td style={{ padding: '8px 12px' }}>
                            <span style={{ fontSize: '10px', padding: '2px 6px', borderRadius: '10px', background: 'rgba(99,102,241,0.1)', color: '#a5b4fc' }}>
                              {c.subsidiary_id || '-'}
                            </span>
                          </td>
                          <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                            {state === 'done' || c.is_imported ? (
                              <span style={importBtnStyle('done')}>✓ {f?.imported || 'Imported'}</span>
                            ) : state === 'loading' ? (
                              <span style={importBtnStyle('loading')}>⏳</span>
                            ) : state === 'error' ? (
                              <button style={importBtnStyle('error')} onClick={() => importClient(c.id)}>⚠ {f?.retry || 'Retry'}</button>
                            ) : (
                              <button style={importBtnStyle()} onClick={() => importClient(c.id)}>+ {f?.import || 'Import'}</button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>

            {/* Pagination */}
            {pagination.total_pages > 1 && (
              <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '12px', alignItems: 'center' }}>
                <button
                  onClick={() => searchClients(pagination.page - 1)}
                  disabled={pagination.page <= 1}
                  style={{ background: 'rgba(255,255,255,0.05)', color: pagination.page <= 1 ? '#475569' : '#94a3b8', border: '1px solid rgba(255,255,255,0.1)', padding: '6px 12px', borderRadius: '6px', fontSize: '12px', cursor: pagination.page <= 1 ? 'default' : 'pointer' }}
                >←</button>
                <span style={{ fontSize: '12px', color: '#94a3b8' }}>
                  {pagination.page} / {pagination.total_pages} ({pagination.total} {f?.clients || 'clients'})
                </span>
                <button
                  onClick={() => searchClients(pagination.page + 1)}
                  disabled={pagination.page >= pagination.total_pages}
                  style={{ background: 'rgba(255,255,255,0.05)', color: pagination.page >= pagination.total_pages ? '#475569' : '#94a3b8', border: '1px solid rgba(255,255,255,0.1)', padding: '6px 12px', borderRadius: '6px', fontSize: '12px', cursor: pagination.page >= pagination.total_pages ? 'default' : 'pointer' }}
                >→</button>
              </div>
            )}
          </>
        )}

        {/* Footer */}
        <div style={{ display: 'flex', justifyContent: isRTL ? 'flex-start' : 'flex-end', marginTop: '16px' }}>
          <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.05)', color: '#94a3b8', border: '1px solid rgba(255,255,255,0.1)', padding: '8px 20px', borderRadius: '8px', fontWeight: '600', fontSize: '13px', cursor: 'pointer' }}>
            {f?.close || 'Close'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ERPImportModal;
